import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ImageUpload from "@/components/admin/ImageUpload";

const WA_VARS = ["{store_name}", "{nama_produk}", "{kategori}", "{tipe_hp}", "{jumlah}", "{harga}", "{total}", "{url_gambar}", "{url_produk}"];

function Field({ label, value, onChange, type = "text", placeholder, testid, textarea, rows }) {
  return (
    <div>
      <Label>{label}</Label>
      {textarea ? (
        <Textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows || 3} className="mt-1" />
      ) : (
        <Input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1" />
      )}
    </div>
  );
}

export default function SettingsManager() {
  const { refresh } = useStore();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)).catch(() => {}); }, []);
  if (!form) return <p className="text-sm text-muted-foreground">Memuat...</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStep = (i, v) => setForm((f) => { const arr = [...(f.how_to_order || [])]; arr[i] = v; return { ...f, how_to_order: arr }; });
  const setFeature = (i, key, v) => setForm((f) => { const arr = [...(f.features || [])]; arr[i] = { ...arr[i], [key]: v }; return { ...f, features: arr }; });
  const addStep = () => setForm((f) => ({ ...f, how_to_order: [...(f.how_to_order || []), ""] }));
  const removeStep = (i) => setForm((f) => ({ ...f, how_to_order: (f.how_to_order || []).filter((_, idx) => idx !== i) }));
  const addFeature = () => setForm((f) => ({ ...f, features: [...(f.features || []), { title: "", desc: "" }] }));
  const removeFeature = (i) => setForm((f) => ({ ...f, features: (f.features || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const { _id, updated_at, ...payload } = form;
      await api.put("/admin/settings", payload);
      await refresh();
      toast.success("Pengaturan tersimpan");
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Pengaturan Toko</h1>
          <p className="mt-1 text-sm text-muted-foreground">Kelola informasi & tampilan toko.</p>
        </div>
        <button data-testid="admin-save-settings-btn" onClick={save} disabled={busy} className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
        </button>
      </div>

      <Tabs defaultValue="umum" className="mt-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="umum">Umum</TabsTrigger>
          <TabsTrigger value="header">Header & Footer</TabsTrigger>
          <TabsTrigger value="tampilan">Tampilan</TabsTrigger>
          <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="konten">Konten</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="umum" className="space-y-4 pt-4">
          <Field label="Nama Toko" value={form.store_name} onChange={(v) => set("store_name", v)} testid="setting-store-name" />
          <Field label="Alamat" value={form.address} onChange={(v) => set("address", v)} textarea rows={2} />
          <Field label="Jam Operasional" value={form.operating_hours} onChange={(v) => set("operating_hours", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Instagram (username)" value={form.instagram} onChange={(v) => set("instagram", v)} />
            <Field label="TikTok (username)" value={form.tiktok} onChange={(v) => set("tiktok", v)} />
          </div>
          <Field label="Catatan Pengiriman" value={form.shipping_note} onChange={(v) => set("shipping_note", v)} textarea rows={2} />
        </TabsContent>

        <TabsContent value="tampilan" className="space-y-4 pt-4">
          <div className="flex gap-6">
            <ImageUpload label="Logo" value={form.logo_url} onChange={(v) => set("logo_url", v)} testid="upload-logo" />
            <ImageUpload label="Favicon" value={form.favicon_url} onChange={(v) => set("favicon_url", v)} testid="upload-favicon" />
          </div>
          <div>
            <Label>Warna Utama Website</Label>
            <div className="mt-1 flex items-center gap-3">
              <input type="color" data-testid="setting-primary-color" value={form.primary_color || "#0F172A"} onChange={(e) => set("primary_color", e.target.value)} className="h-11 w-16 cursor-pointer rounded-lg border border-border" />
              <Input value={form.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className="max-w-[140px]" />
            </div>
          </div>
          <div>
            <Label>Warna Aksen (highlight & glow futuristik)</Label>
            <div className="mt-1 flex items-center gap-3">
              <input type="color" data-testid="setting-accent-color" value={form.accent_color || "#0D9488"} onChange={(e) => set("accent_color", e.target.value)} className="h-11 w-16 cursor-pointer rounded-lg border border-border" />
              <Input value={form.accent_color || ""} onChange={(e) => set("accent_color", e.target.value)} className="max-w-[140px]" />
            </div>
          </div>
          <div>
            <Label>Warna Panel "Pilih Tipe HP"</Label>
            <div className="mt-1 flex items-center gap-3">
              <input type="color" data-testid="setting-panel-color" value={form.finder_panel_color || "#FAF9F6"} onChange={(e) => set("finder_panel_color", e.target.value)} className="h-11 w-16 cursor-pointer rounded-lg border border-border" />
              <Input value={form.finder_panel_color || ""} onChange={(e) => set("finder_panel_color", e.target.value)} className="max-w-[140px]" />
            </div>
          </div>
          <ImageUpload label="Banner Beranda" value={form.hero_image_url} onChange={(v) => set("hero_image_url", v)} testid="upload-hero" />
          <Field label="Headline Banner" value={form.hero_headline} onChange={(v) => set("hero_headline", v)} />
          <Field label="Subheadline Banner" value={form.hero_subheadline} onChange={(v) => set("hero_subheadline", v)} textarea rows={2} />
          <Field label="Teks Tombol Banner" value={form.hero_cta_text} onChange={(v) => set("hero_cta_text", v)} />
        </TabsContent>

        <TabsContent value="whatsapp" className="space-y-4 pt-4">
          <Field label="Nomor WhatsApp Admin (format internasional, cth: 628123456789)" value={form.whatsapp_number} onChange={(v) => set("whatsapp_number", v)} testid="setting-wa-number" />
          <div>
            <Label>Template Pesan WhatsApp</Label>
            <Textarea data-testid="setting-wa-template" value={form.whatsapp_template || ""} onChange={(v) => set("whatsapp_template", v.target.value)} rows={12} className="mt-1 font-mono text-xs" />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {WA_VARS.map((v) => <span key={v} className="rounded-md bg-secondary px-2 py-0.5 text-xs font-mono text-muted-foreground">{v}</span>)}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Gunakan variabel di atas, akan otomatis diganti saat pelanggan memesan.</p>
          </div>
        </TabsContent>

        <TabsContent value="konten" className="space-y-5 pt-4">
          <Field label="Judul Bagian Keunggulan" value={form.features_title} onChange={(v) => set("features_title", v)} testid="setting-features-title" />
          <div>
            <div className="flex items-center justify-between">
              <Label>Keunggulan Toko</Label>
              <button type="button" data-testid="add-feature-btn" onClick={addFeature} className="flex items-center gap-1 text-xs font-semibold text-accent hover:underline"><Plus className="h-3.5 w-3.5" /> Tambah</button>
            </div>
            <div className="mt-1.5 space-y-2">
              {(form.features || []).map((f, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="grid flex-1 grid-cols-2 gap-2">
                    <Input value={f.title} onChange={(e) => setFeature(i, "title", e.target.value)} placeholder="Judul" />
                    <Input value={f.desc} onChange={(e) => setFeature(i, "desc", e.target.value)} placeholder="Deskripsi" />
                  </div>
                  <button type="button" onClick={() => removeFeature(i)} className="mt-1.5 rounded-lg p-2 text-destructive hover:bg-destructive/10"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <Field label="Judul Bagian Cara Pemesanan" value={form.how_to_order_title} onChange={(v) => set("how_to_order_title", v)} testid="setting-howto-title" />
          <div>
            <div className="flex items-center justify-between">
              <Label>Langkah Cara Pemesanan</Label>
              <button type="button" data-testid="add-step-btn" onClick={addStep} className="flex items-center gap-1 text-xs font-semibold text-accent hover:underline"><Plus className="h-3.5 w-3.5" /> Tambah</button>
            </div>
            <div className="mt-1.5 space-y-2">
              {(form.how_to_order || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{i + 1}.</span>
                  <Input value={s} onChange={(e) => setStep(i, e.target.value)} />
                  <button type="button" onClick={() => removeStep(i)} className="rounded-lg p-2 text-destructive hover:bg-destructive/10"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="seo" className="space-y-4 pt-4">
          <Field label="Judul Website (SEO)" value={form.seo_title} onChange={(v) => set("seo_title", v)} />
          <Field label="Deskripsi Website (SEO)" value={form.seo_description} onChange={(v) => set("seo_description", v)} textarea rows={2} />
          <Field label="Kata Kunci (SEO)" value={form.seo_keywords} onChange={(v) => set("seo_keywords", v)} />
        </TabsContent>

        <TabsContent value="header" className="space-y-4 pt-4">
          <Field label="Teks Pemberitahuan (banner atas header)" value={form.announcement_text} onChange={(v) => set("announcement_text", v)} placeholder="Kosongkan untuk menyembunyikan banner" testid="setting-announcement" />
          <Field label="Judul Pencari Beranda" value={form.finder_title} onChange={(v) => set("finder_title", v)} testid="setting-finder-title" />
          <Field label="Subjudul Pencari Beranda" value={form.finder_subtitle} onChange={(v) => set("finder_subtitle", v)} textarea rows={2} testid="setting-finder-subtitle" />
          <Field label="Deskripsi Footer" value={form.footer_description} onChange={(v) => set("footer_description", v)} textarea rows={2} testid="setting-footer-desc" />
          <Field label="Teks Copyright Footer" value={form.footer_copyright} onChange={(v) => set("footer_copyright", v)} placeholder="Kosongkan untuk otomatis" testid="setting-footer-copyright" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
